import { useState, useEffect } from 'react';
import { ArrowUpRight, ArrowDownRight, Plus, Trash2, Pencil, Search, Mail, Filter, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Drawer, DrawerContent, DrawerTitle } from '../components/ui/drawer';
import { formatAmount, formatDate, formatDayLabel, getIcon, catColor, catTint } from '../lib/constants';
import { deleteTransaction, dismissPossibleDuplicate } from '../lib/api';
import { toast } from 'sonner';
import TransactionDialog from '../components/TransactionDialog';
import SwipeableRow, { isTouchDevice } from '../components/SwipeableRow';
import { DateRangePicker } from '../components/DateRangePicker';
import Pagination from '../components/Pagination';
import { useAmountsVisibility } from '../context/AmountsVisibilityContext';
import { Skeleton } from '../components/ui/skeleton';
import { ProfileButton } from '../components/Sidebar';
import { useLocalTransactions } from '../context/LocalTransactionsContext';

const PAGE_SIZE = 10;

export default function Transactions({ transactions, dataLoading = false, categories, goals = [], onRefresh, dateRange, setDateRange, budgetPeriod, onOpenCategories }) {
  const local = useLocalTransactions();
  const { hidden } = useAmountsVisibility();
  const [showAdd, setShowAdd] = useState(false);
  const [editTxn, setEditTxn] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyDetected, setOnlyDetected] = useState(false);
  const [page, setPage] = useState(1);
  // Mobile-only — type/category/detected used to always render inline,
  // stacking into 4 full-width rows on small screens (title, date range,
  // search, type, category, detected = 6 rows before any transaction was
  // visible). Desktop keeps the always-visible layout below (no crowding
  // problem at that width); mobile collapses these three into a Drawer
  // behind a single "Filtros" button with an active-count badge.
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilterCount = (filterType !== 'all' ? 1 : 0) + (filterCategory !== 'all' ? 1 : 0) + (onlyDetected ? 1 : 0);

  const filtered = transactions.filter(txn => {
    if (filterType !== 'all' && txn.type !== filterType) return false;
    if (filterCategory !== 'all' && txn.category_id !== filterCategory) return false;
    if (onlyDetected && txn.source !== 'email') return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return (txn.description || '').toLowerCase().includes(q) || (txn.category_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // Reset to the first page whenever the filtered set changes so we never land
  // on an out-of-range page.
  useEffect(() => { setPage(1); }, [filterType, filterCategory, onlyDetected, searchQuery, transactions]);
  const safePage = Math.min(page, pageCount);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // Group the current page by calendar day — a header shows the date once
  // (plus that day's own net total, independent of whatever budget period
  // is selected — it's just a same-day sum, purely to help the user read
  // their numbers) instead of repeating the date on every single row.
  const dayGroups = [];
  for (const txn of paged) {
    const key = txn.date.slice(0, 10);
    const last = dayGroups[dayGroups.length - 1];
    if (last && last.key === key) {
      last.items.push(txn);
    } else {
      dayGroups.push({ key, date: txn.date, items: [txn] });
    }
  }

  // When a specific category is filtered, surface the total across every
  // matching transaction being viewed (the full filtered set, not just the
  // current page) — the one thing that actually answers "how much have I
  // spent/earned in this category," regardless of which date range happens
  // to be selected.
  const activeCategory = filterCategory !== 'all' ? categories.find(c => c.id === filterCategory) : null;
  const categoryTotal = activeCategory
    ? filtered.reduce((sum, t) => sum + (t.type === 'income' ? t.amount : -t.amount), 0)
    : 0;

  // Confirmed before deleting — a real "genuinely destructive, irreversible
  // action" per the apple-design skill's own criteria (no undo, and the
  // edit/delete icons sit right next to each other, an easy mis-tap
  // especially on mobile).
  const handleDelete = async (txn) => {
    const label = txn.description || txn.category_name || 'esta transacción';
    if (!window.confirm(`¿Eliminar ${label} (${formatAmount(txn.amount, false)})? Esta acción no se puede deshacer.`)) return;
    // Optimistic: gone from the list at once; the refetch restores it if the
    // delete failed.
    local.remove(txn.id);
    toast.success('Transaccion eliminada');
    try {
      await deleteTransaction(txn.id);
    } catch (e) {
      toast.error('No se pudo eliminar. Intenta de nuevo.');
    } finally {
      onRefresh();
    }
  };

  const categoryTypes = new Map(categories.map(c => [c.id, c.type]));

  // Possible duplicates are only ever flagged by the backend, never removed —
  // the user settles them here: delete this copy, or keep it and clear the flag.
  const handleNotDuplicate = async (txn) => {
    try {
      await dismissPossibleDuplicate(txn.id);
      toast.success('Listo, lo dejamos como movimiento aparte');
      onRefresh();
    } catch (e) {
      toast.error('No se pudo actualizar');
    }
  };

  return (
    <div className="space-y-6" data-testid="transactions-page">
      {/* Title + filters stick to the top together — same pattern as
          Dashboard's header, extended here since this page's filter row is
          exactly what was called out as needing to stay put too. */}
      <div className="sticky top-[env(safe-area-inset-top)] z-10 bg-surface pb-4 space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="w-full sm:w-auto">
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Movimientos</h1>
            <ProfileButton className="-mr-1.5" />
          </div>
          {/* While the list isn't for the selected range yet, never claim a
              count (or "No hay transacciones") that's about to change. */}
          {dataLoading
            ? <Skeleton className="h-4 w-28 mt-2 bg-white/[0.06]" />
            : <p className="text-slate-400 text-sm mt-1">{filtered.length} transacciones</p>}
        </div>
        <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} budgetPeriod={budgetPeriod} />
      </div>

      {/* Filters */}
      <div className="flex gap-3" data-testid="transaction-filters">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            data-testid="transaction-search"
            placeholder="Buscar transacciones..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="bg-slate-900/70 border-slate-700 pl-9 h-10 rounded-xl text-white placeholder:text-slate-400"
          />
        </div>

        {/* Desktop: no crowding problem at this width, keep filters always visible */}
        <div className="hidden sm:flex gap-3">
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="bg-slate-900/70 border-slate-700 h-10 rounded-xl text-white w-36" data-testid="filter-type-select">
              <SelectValue placeholder="Tipo" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="income">Ingresos</SelectItem>
              <SelectItem value="expense">Gastos</SelectItem>
              <SelectItem value="goal">Aportes a metas</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="bg-slate-900/70 border-slate-700 h-10 rounded-xl text-white w-44" data-testid="filter-category-select">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all">Todas las categorias</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            type="button"
            data-testid="filter-detected-toggle"
            onClick={() => setOnlyDetected(v => !v)}
            className={`hit-44 flex items-center gap-1.5 h-10 px-3.5 rounded-xl text-sm font-medium border transition-colors shrink-0 ${
              onlyDetected ? 'bg-violet-500/15 border-violet-500/40 text-violet-300' : 'bg-slate-900/70 border-slate-700 text-slate-400 hover:bg-white/5'
            }`}
          >
            <Mail className="w-3.5 h-3.5" /> Detectadas
          </button>
        </div>

        {/* Mobile: collapsed into a Drawer behind one button */}
        <button
          type="button"
          data-testid="filters-open-btn"
          onClick={() => setFiltersOpen(true)}
          className={`sm:hidden relative flex items-center gap-1.5 h-10 px-3.5 rounded-xl text-sm font-medium border transition-colors shrink-0 ${
            activeFilterCount > 0 ? 'bg-violet-500/15 border-violet-500/40 text-violet-300' : 'bg-slate-900/70 border-slate-700 text-slate-400 hover:bg-white/5'
          }`}
        >
          <Filter className="w-4 h-4" /> Filtros
          {activeFilterCount > 0 && (
            <span className="w-4 h-4 rounded-full bg-violet-500 text-on-brand text-[10px] font-bold flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Entry point to Categorías (no longer its own nav item) — same
            pill style as the Filtros/Detectadas buttons beside it. */}
        {onOpenCategories && (
          <button
            type="button"
            data-testid="open-categories-btn"
            onClick={onOpenCategories}
            className="hit-44 flex items-center gap-1.5 h-10 px-3.5 rounded-xl text-sm font-medium border transition-colors shrink-0 bg-slate-900/70 border-slate-700 text-slate-400 hover:bg-white/5 active:scale-[0.97]"
          >
            <Tag className="w-4 h-4" /> Categorías
          </button>
        )}
      </div>
      </div>

      <Drawer open={filtersOpen} onOpenChange={setFiltersOpen}>
        <DrawerContent className="bg-slate-900 border-white/10 text-slate-200 pb-safe" data-testid="filters-drawer">
          <DrawerTitle className="sr-only">Filtros</DrawerTitle>
          <div className="flex items-center justify-between px-5 pb-3">
            <span className="text-sm font-semibold text-white">Filtros</span>
            {activeFilterCount > 0 && (
              <button
                type="button"
                data-testid="filters-clear-btn"
                onClick={() => { setFilterType('all'); setFilterCategory('all'); setOnlyDetected(false); }}
                className="text-xs font-medium text-violet-400 hover:text-violet-300"
              >
                Limpiar
              </button>
            )}
          </div>
          <div className="px-5 pb-6 space-y-3">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="bg-slate-950/50 border-slate-700 h-11 rounded-xl text-white w-full" data-testid="filter-type-select-mobile">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="income">Ingresos</SelectItem>
                <SelectItem value="expense">Gastos</SelectItem>
              <SelectItem value="goal">Aportes a metas</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="bg-slate-950/50 border-slate-700 h-11 rounded-xl text-white w-full" data-testid="filter-category-select-mobile">
                <SelectValue placeholder="Categoria" />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="all">Todas las categorias</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              data-testid="filter-detected-toggle-mobile"
              onClick={() => setOnlyDetected(v => !v)}
              className={`w-full flex items-center gap-1.5 h-11 px-3.5 rounded-xl text-sm font-medium border transition-colors ${
                onlyDetected ? 'bg-violet-500/15 border-violet-500/40 text-violet-300' : 'bg-slate-950/50 border-slate-700 text-slate-400 hover:bg-white/5'
              }`}
            >
              <Mail className="w-4 h-4" /> Detectadas
            </button>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Category filter total — the running total for every matching
          transaction currently being viewed, not just this page. */}
      {activeCategory && (
        <div className="glass-card rounded-xl px-4 py-3 flex items-center justify-between" data-testid="category-filter-total">
          <span className="text-sm text-slate-400">Total en {activeCategory.name}</span>
          <span className={`text-lg font-bold metric-value ${categoryTotal >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            {categoryTotal >= 0 ? '+' : '-'}{formatAmount(Math.abs(categoryTotal), hidden)}
          </span>
        </div>
      )}

      {/* Transaction List — grouped by day; each group's header carries the
          date and that day's own net total, so rows below it only need to
          show their core data (no per-row date repetition). */}
      <div className="space-y-5">
        {dataLoading ? (
          <div className="space-y-2" data-testid="transactions-loading">
            <Skeleton className="h-4 w-20 bg-white/[0.06]" />
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-[72px] w-full rounded-xl bg-white/[0.04]" />
            ))}
          </div>
        ) : filtered.length > 0 ? dayGroups.map((group, gi) => {
          const groupTotal = group.items.reduce((sum, t) => sum + (t.type === 'income' ? t.amount : -t.amount), 0);
          return (
            <div key={group.key} data-testid={`day-group-${group.key}`}>
              <div className="flex items-center justify-between px-1 mb-2">
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{formatDayLabel(group.date)}</span>
                {/* Deliberately neutral, not red/green — a plain same-day
                    sum for context, not a judgment on the day. */}
                <span className="text-xs font-semibold metric-value text-slate-300">
                  {groupTotal >= 0 ? '+' : '-'}{formatAmount(Math.abs(groupTotal), hidden)}
                </span>
              </div>
              <div className="space-y-2">
                {group.items.map((txn, i) => {
                  const Icon = getIcon(txn.category_icon);
                  const isIncome = txn.type === 'income';
                  // Goal contributions (aportes): not editable here (edit
                  // the goal from Metas), shown in neutral — not spending.
                  const isGoal = txn.type === 'goal';
                  return (
                    <div key={txn.id}>
                    <SwipeableRow
                      testId={`swipe-txn-${txn.id}`}
                      disabled={!isTouchDevice}
                      leftAction={isGoal ? undefined : { icon: Pencil, bg: 'bg-violet-500', onCommit: () => { setEditTxn(txn); setShowAdd(true); } }}
                      rightAction={{ icon: Trash2, bg: 'bg-rose-500', onCommit: () => handleDelete(txn) }}
                      peekContent={
                        <div className="text-center">
                          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: catTint(txn.category_color) }}>
                            <Icon className="w-6 h-6" style={{ color: catColor(txn.category_color) }} />
                          </div>
                          <p className="text-lg font-semibold text-white">{txn.description || txn.category_name}</p>
                          <p className="text-sm text-slate-400 mt-1">{txn.category_name} &middot; {formatDate(txn.date)}</p>
                          <p className={`text-2xl font-bold mt-3 metric-value ${isIncome ? 'text-emerald-400' : isGoal ? 'text-violet-300' : 'text-rose-400'}`}>
                            {isIncome ? '+' : '-'}{formatAmount(txn.amount, hidden)}
                          </p>
                        </div>
                      }
                    >
                      <div
                        className={`glass-card rounded-xl p-4 flex items-center justify-between hover:bg-white/[0.02] transition-colors duration-200 animate-fade-in-up stagger-${Math.min(i + 1, 5)}`}
                        style={{ opacity: 0 }}
                        data-testid={`transaction-${txn.id}`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: catTint(txn.category_color) }}>
                            <Icon className="w-4.5 h-4.5" style={{ color: catColor(txn.category_color) }} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-200 truncate flex items-center gap-1.5">
                              {txn.description || txn.category_name}
                              {txn.source === 'email' && (
                                <Mail className="w-3 h-3 text-violet-400 shrink-0" title="Detectado automáticamente" />
                              )}
                            </p>
                            <p className="text-xs text-slate-500 truncate">{isGoal ? `Aporte a meta · ${txn.category_name}` : txn.category_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="flex items-center gap-1">
                            {isIncome ? (
                              <ArrowUpRight className="w-3.5 h-3.5 text-emerald-400" />
                            ) : isGoal ? null : (
                              <ArrowDownRight className="w-3.5 h-3.5 text-rose-400" />
                            )}
                            <span className={`text-sm font-semibold metric-value ${isIncome ? 'text-emerald-400' : isGoal ? 'text-slate-200' : 'text-rose-400'}`}>
                              {isIncome ? '+' : '-'}{formatAmount(txn.amount, hidden)}
                            </span>
                          </div>
                          {/* Touch devices get swipe-to-reveal instead (see SwipeableRow
                              above) — keeping both would be redundant and cluttered. */}
                          {!isTouchDevice && (
                            <div className="flex items-center gap-1">
                              <button
                                data-testid={`edit-txn-${txn.id}`}
                                disabled={isGoal}
                                title={isGoal ? 'Los aportes se gestionan desde Metas' : undefined}
                                onClick={() => { setEditTxn(txn); setShowAdd(true); }}
                                className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:text-white hover:bg-white/10 transition-colors duration-200 disabled:opacity-30 disabled:pointer-events-none"
                              >
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button
                                data-testid={`delete-txn-${txn.id}`}
                                onClick={() => handleDelete(txn)}
                                className="w-9 h-9 rounded-lg flex items-center justify-center text-slate-500 hover:text-rose-400 hover:bg-rose-400/10 transition-colors duration-200"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </SwipeableRow>
                    {txn.possible_duplicate_of && (
                      <div
                        className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-amber-400/10 px-4 py-2 text-xs"
                        data-testid={`possible-duplicate-${txn.id}`}
                      >
                        <span className="flex-1 min-w-0 text-amber-300">
                          ¿Duplicado? Hay otro movimiento igual ese mismo día.
                        </span>
                        <button
                          onClick={() => handleDelete(txn)}
                          className="min-h-[44px] px-2 font-semibold text-rose-300 hover:text-rose-200"
                          data-testid={`duplicate-delete-${txn.id}`}
                        >
                          Eliminar
                        </button>
                        <button
                          onClick={() => handleNotDuplicate(txn)}
                          className="min-h-[44px] px-2 font-semibold text-slate-300 hover:text-white"
                          data-testid={`duplicate-dismiss-${txn.id}`}
                        >
                          No es duplicado
                        </button>
                      </div>
                    )}
                    {/* Expense filed under an income-only category (or the
                        reverse) — shown, never moved automatically; fixing it
                        here also teaches the merchant memory. */}
                    {(() => {
                      const catType = categoryTypes.get(txn.category_id);
                      const misfiled = (txn.type === 'expense' && catType === 'income') || (txn.type === 'income' && catType === 'expense');
                      if (!misfiled) return null;
                      return (
                        <div
                          className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-amber-400/10 px-4 py-2 text-xs"
                          data-testid={`misfiled-${txn.id}`}
                        >
                          <span className="flex-1 min-w-0 text-amber-300">
                            {txn.type === 'expense' ? 'Gasto' : 'Ingreso'} en una categoría de {txn.type === 'expense' ? 'ingresos' : 'gastos'} ({txn.category_name}).
                          </span>
                          <button
                            onClick={() => { setEditTxn(txn); setShowAdd(true); }}
                            className="min-h-[44px] px-2 font-semibold text-violet-300 hover:text-violet-200"
                            data-testid={`misfiled-fix-${txn.id}`}
                          >
                            Cambiar categoría
                          </button>
                        </div>
                      );
                    })()}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }) : (
          <div className="text-center py-16">
            <p className="text-slate-400 mb-4">No hay transacciones</p>
            <Button
              data-testid="add-first-transaction-btn"
              onClick={() => { setEditTxn(null); setShowAdd(true); }}
              className="bg-violet-500 hover:bg-violet-600 text-on-brand rounded-full px-6"
            >
              Agregar transaccion
            </Button>
          </div>
        )}
      </div>

      {!dataLoading && <Pagination
        page={safePage}
        pageCount={pageCount}
        onPageChange={setPage}
        total={filtered.length}
        pageSize={PAGE_SIZE}
      />}

      <TransactionDialog
        open={showAdd}
        onOpenChange={(open) => { setShowAdd(open); if (!open) setEditTxn(null); }}
        categories={categories}
        goals={goals}
        editTransaction={editTxn}
        onSuccess={onRefresh}
      />
    </div>
  );
}
